/**
 * 알림 시스템 타입 정의
 */

import { PriorityCodeLabel } from '@/common/constants/priority';
import { ReportType, ReportTypeLabel } from '@/common/constants/report';
import { StatusCodeLabel } from '@/common/constants/status';
import { TodoStatusCodeLabel } from '@/common/constants/todo';
import { dateRangeDisplay } from '@/common/utils/dateUtils';
import { Group, Pill, Text } from '@mantine/core';
import {
    Icon,
    IconChecklist,
    IconPresentation,
    IconProps,
    IconSwipe,
    IconUserCircle,
    IconUsers,
} from '@tabler/icons-react';
import React, { ForwardRefExoticComponent, RefAttributes } from 'react';
import { Trans } from 'react-i18next';
import { limitNotificationMessage } from '../utils/notificationUtils';

// goal, project, todo 업데이트 시 사용하는 인터페이스
export interface UpdateContext<T> {
    oldData: T;
    changeTypes: {
        isPriorityChange?: boolean | null;
        isStatusChange?: boolean | null;
        isDeadlineChange?: boolean | null;
        isLeaderChange?: boolean | null;
        isTimeBlockChange?: boolean | null;
        isCommentChange?: boolean | null;
    };
}

// 알림 유형 열거형
export enum NotificationType {
    // 초대 관련 알림
    WORKSPACE_INVITE = 'workspace_invite', // 워크스페이스 초대
    WORKSPACE_JOIN = 'workspace_join', // 워크스페이스 참여 확인
    TEAM_INVITE = 'team_invite', // 팀 초대

    // 방출 관련 알림
    WORKSPACE_OUT = 'workspace_out', // 워크스페이스에서 방출
    TEAM_OUT = 'team_out', // 팀에서 방출

    // 목표 관련 알림
    GOAL_PRIORITY_CHANGED = 'goal_priority_changed', // 목표 우선순위 변경
    GOAL_STATE_CHANGED = 'goal_state_changed', // 목표 상태 변경
    GOAL_DEADLINE_CHANGED = 'goal_deadline_changed', // 목표 마감기한 변경
    GOAL_LEADER_CHANGED = 'goal_leader_changed', // 목표 관리자 변경
    GOAL_DEADLINE_REMINDER = 'goal_deadline_reminder', // 목표 마감기한 알림

    // 할 일 관련 알림
    TODO_ASSIGNED = 'todo_assigned', // 할 일 할당
    // TODO_TITLE_CHANGED = 'todo_title_changed', // 할 일 제목 변경
    TODO_STATE_CHANGED = 'todo_state_changed', // 할 일 상태 변경
    TODO_DEADLINE_CHANGED = 'todo_deadline_changed', // 할 일 마감기한 변경
    TODO_DEADLINE_REMINDER = 'todo_deadline_reminder', // 할 일 마감기한 알림
    TODO_TIME_CHANGED = 'todo_time_changed', // 할 일 시간 변경

    // 프로젝트 관련 알림
    PROJECT_PRIORITY_CHANGED = 'project_priority_changed', // 프로젝트 우선순위 변경
    PROJECT_STATE_CHANGED = 'project_state_changed', // 프로젝트 상태 변경
    PROJECT_DEADLINE_CHANGED = 'project_deadline_changed', // 프로젝트 마감기한 변경
    PROJECT_LEADER_CHANGED = 'project_leader_changed', // 프로젝트 책임자 변경
    PROJECT_DEADLINE_REMINDER = 'project_deadline_reminder', // 프로젝트 마감기한 알림

    // 커뮤니케이션 관련 알림
    COMMENT = 'comment', // 댓글 알림
    REPLY = 'reply', // 답글 알림
    MENTION = 'mention', // 노트 멘션 알림
    COMMENT_MENTION = 'comment_mention', // 댓글 멘션 알림 (goal, todo, note)

    // 댓글 및 답글까지 함께 처리
    FEEDBACK_COMMENT = 'feedback_comment', // 피드백 댓글 알림
    DAILY_SCRUM_COMMENT = 'daily_scrum_comment', // 데일리스크럼 댓글 알림

    // 성과관리 관련 알림
    REPORT_RECEIVE = 'report_receive', // 보고서 수신 알림
    REPORT_UPDATED = 'report_updated', // 받은 보고서 수정 알림
    REPORT_REQUEST = 'report_request', // 보고서 요청 알림
    FEEDBACK_RECEIVED = 'feedback_received', // 피드백 수신 알림
    FEEDBACK_UPDATED = 'feedback_updated', // 피드백 수정 알림
    FEEDBACK_REQUEST = 'feedback_request', // 피드백 요청 알림
    FEEDBACK_REQUEST_UPDATED = 'feedback_request_updated', // 피드백 요청 수정 알림
    FEEDBACK_REQUEST_REJECTED = 'feedback_request_rejected', // 피드백 요청 거절 알림
    FEEDBACK_REACTION = 'feedback_reaction', // 피드백 이모지 리액션 알림

    // 계획 수립 알림
    PLAN_TIME = 'plan_time', // 계획 수립 알림
    PLAN_REQUEST = 'plan_request', // 계획 수립 요청 알림

    // 시스템 알림
    SYSTEM_ANNOUNCEMENT = 'system_announcement', // 시스템 공지
    ACCOUNT_SECURITY = 'account_security', // 계정 보안 알림
}

// 알림 콘텐츠 타입 (유니온 타입)
export type NotificationContent =
    | WorkspaceInviteContent
    | WorkspaceJoinContent
    | TeamInviteContent
    | WorkspaceOutContent
    | TeamOutContent
    | GoalPriorityChangedContent
    | GoalStateChangedContent
    | GoalDeadlineChangedContent
    | GoalLeaderChangedContent
    | GoalDeadlineReminderContent
    | TodoAssignedContent
    | TodoStateChangedContent
    | TodoDeadlineChangedContent
    | TodoDeadlineReminderContent
    | TodoTimeChangedContent
    | ProjectPriorityChangedContent
    | ProjectStateChangedContent
    | ProjectDeadlineChangedContent
    | ProjectLeaderChangedContent
    | ProjectDeadlineReminderContent
    | CommentContent
    | ReplyContent
    | MentionContent
    | CommentMentionContent
    | FeedbackCommentContent
    | DailyScrumCommentContent
    | ReportContent
    | ReportRequestContent
    | FeedbackReceivedContent
    | FeedbackRequestContent
    | FeedbackRequestRejectedContent
    | FeedbackReactionContent
    | PlanTimeContent
    | PlanRequestContent
    | SystemAnnouncementContent
    | AccountSecurityContent;

// 워크스페이스 초대 알림 콘텐츠
export interface WorkspaceInviteContent {
    workspace_name: string;
    workspace_image_url: string;
    inviter_name: string;
    inviter_id: string;
    invitation_id: string; // 초대장 ID (member_invitation 데이터 ID)
}

// 워크스페이스 초대 수락 알림 콘텐츠
export interface WorkspaceJoinContent {
    workspace_name: string;
    joiner_name: string; // 초대 수락한 사람 이름
    joiner_image_url: string; // 초대 수락한 사람 이미지 URL
    joiner_id: string; // 초대 수락한 사람 UID
}

// 워크스페이스 방출 관련 인터페이스
export interface WorkspaceOutContent {
    workspace_id: string;
    workspace_name: string;
    workspace_image_url: string;
    actor_id?: string;
    actor_name?: string;
}

// 팀 초대 알림 콘텐츠
export interface TeamInviteContent {
    team_name: string;
    team_image_url: string;
    workspace_name: string;
    inviter_name: string;
    inviter_id: string;
}

// 팀 방출 관련 인터페이스
export interface TeamOutContent {
    team_id: string;
    team_name: string;
    team_image_url: string;
    workspace_id: string;
    workspace_name: string;
    actor_id?: string;
    actor_name?: string;
}

// 목표 우선순위 변경 알림 콘텐츠
export interface GoalPriorityChangedContent {
    goal_title: string;
    old_priority: string;
    new_priority: string;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 목표 상태 변경 알림 콘텐츠
export interface GoalStateChangedContent {
    goal_title: string;
    old_state: string;
    new_state: string;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 목표 마감기한 변경 알림 콘텐츠
export interface GoalDeadlineChangedContent {
    goal_title: string;
    old_start_date: string | null;
    old_end_date: string | null;
    new_start_date: string | null;
    new_end_date: string | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 목표 담당자 변경 알림 콘텐츠
export interface GoalLeaderChangedContent {
    goal_title: string;
    old_leader_name: string | null;
    old_leader_id: string | null;
    new_leader_name: string | null;
    new_leader_id: string | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 목표 마감기한 알림 콘텐츠
export interface GoalDeadlineReminderContent {
    title: string; // goal title
    deadline: string; // 마감기한 (ISO 문자열)
    deadline_status: 'today' | 'overdue' | 'upcoming';
    days_delta: number; // 기준일로부터 차이 (무조건 양수)
}

// 할 일 할당 알림 콘텐츠
export interface TodoAssignedContent {
    todo_title: string;
    assigner_name: string;
    assigner_image_url: string;
    assigner_id: string;
    goal_title?: string;
    goal_id?: string;
}

// 할 일 상태 변경 알림 콘텐츠
export interface TodoStateChangedContent {
    todo_title: string;
    old_state: string;
    new_state: string;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 할 일 마감기한 변경 알림 콘텐츠
export interface TodoDeadlineChangedContent {
    todo_title: string;
    old_deadline: string | null;
    new_deadline: string | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 할 일 제목 변경 알림 콘텐츠
// export interface TodoTitleChangedContent {
//     old_title: string;
//     new_title: string;
//     changer_name: string;
//     changer_id: string;
// }

// 할 일 마감기한 알림 콘텐츠
export interface TodoDeadlineReminderContent {
    title: string; // todo title
    deadline: string; // 마감기한 (ISO 문자열)
    deadline_status: 'today' | 'overdue' | 'upcoming';
    days_delta: number; // 기준일로부터 차이 (무조건 양수)
}

// 할 일 시간 변경 알림 콘텐츠
export interface TodoTimeChangedContent {
    todo_title: string;
    planner_id: string;
    old_time_block: number[] | null;
    new_time_block: number[] | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 프로젝트 우선순위 변경 알림 콘텐츠
export interface ProjectPriorityChangedContent {
    project_title: string;
    old_priority: string;
    new_priority: string;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 프로젝트 상태 변경 알림 콘텐츠
export interface ProjectStateChangedContent {
    project_title: string;
    old_state: string;
    new_state: string;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 프로젝트 마감기한 변경 알림 콘텐츠
export interface ProjectDeadlineChangedContent {
    project_title: string;
    old_start_date: string | null;
    old_end_date: string | null;
    new_start_date: string | null;
    new_end_date: string | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 프로젝트 책임자 변경 알림 콘텐츠
export interface ProjectLeaderChangedContent {
    project_title: string;
    old_leader_name: string | null;
    old_leader_id: string | null;
    new_leader_name: string | null;
    new_leader_id: string | null;
    changer_name: string;
    changer_image_url: string;
    changer_id: string;
}

// 프로젝트 마감기한 알림 콘텐츠
export interface ProjectDeadlineReminderContent {
    title: string; // project title
    deadline: string; // 마감기한 (ISO 문자열)
    deadline_status: 'today' | 'overdue' | 'upcoming';
    days_delta: number; // 기준일로부터 차이 (무조건 양수)
}

// 댓글 알림 콘텐츠
export interface CommentContent {
    commenter_name: string;
    commenter_image_url: string;
    commenter_id: string;
    comment_text: string;
    parent_type: string; // 어떤 컨텐츠에 달린 댓글인지 (todo, goal, project, note)
    parent_title: string;
    parent_id: string;
}

// 답글 알림 콘텐츠
export interface ReplyContent {
    replier_name: string;
    replier_image_url: string;
    replier_id: string;
    reply_text: string;
    comment_id: string; // 댓글 id
    parent_type: string; // 어떤 컨텐츠에 달린 답글인지 (todo, goal, project)
    parent_title: string;
    parent_id: string;
}

// 댓글 멘션 알림 콘텐츠
export interface CommentMentionContent {
    mentioner_name: string;
    mentioner_image_url: string;
    mentioner_id: string;
    comment_id: string;
    comment_text: string;
    parent_type: string; // todo, goal, note
    parent_id: string;
    parent_title: string;
}

// 노트 멘션 알림 콘텐츠
export interface MentionContent {
    mention_id: string; // 노트 컨텐츠 내 블럭의 ID
    mentioner_name: string;
    mentioner_image_url: string;
    mentioner_id: string;
    // mentionText: string;
    context_type: string;
    context_title: string;
    context_id: string;
}

// 피드백 댓글/답글 알림 콘텐츠
export interface FeedbackCommentContent {
    is_feedback_receiver: boolean; // 알림 수신자가 피드백 수신자인지 여부 (클릭 후 이동할 탭 체크)
    comment_id: string; // 댓글(답글) ID
    comment_text: string; // 댓글(답글) 내용 일부
    is_reply: boolean; // true이면 답글
    parent_comment_id: string; // 답글일 경우, 부모 댓글 ID
    commenter_name: string;
    commenter_image_url: string;
    commenter_id: string;
    feedback_id: string; // 댓글이 달린 피드백의 ID
    feedback_content: string; // 댓글이 달린 피드백의 내용 일부
    workspace_id: string;
    workspace_name: string;
    workspace_image_url: string;
}

// 데일리스크럼 댓글/답글 알림 콘텐츠
export interface DailyScrumCommentContent {
    comment_id: string; // 댓글(답글) ID
    comment_text: string; // 댓글(답글) 내용 일부
    is_reply: boolean; // true이면 답글
    parent_comment_id: string; // 답글일 경우, 부모 댓글 ID
    daily_scrum_id: string; // 데일리스크럼 ID
    daily_scrum_date: string; // 데일리스크럼 날짜
    daily_scrum_item_id: string; // 데일리스크럼 아이템 ID
    // daily_scrum_content: string; // 데일리스크럼 내용 일부 - 필요 없을 시 제거
    commenter_name: string;
    commenter_image_url: string;
    commenter_id: string;
    team_id: string;
    // team_name: string;
    // team_image_url: string;
}

// 보고서 수신 알림 콘텐츠
export interface ReportContent {
    writer_id: string; // 작성자 ID
    writer_name: string; // 작성자 이름
    writer_image_url: string; // 작성자 이미지 URL
    report_type: ReportType; // daily / weekly
    report_title: string; // 보고서 제목
    workspace_id: string; // 워크스페이스 ID
    workspace_name: string; // 워크스페이스 이름
    workspace_image_url: string; // 워크스페이스 이미지 URL
}

// 보고서 요청 알림 콘텐츠
export interface ReportRequestContent {
    requester_id: string; // 요청자 ID
    requester_name: string; // 요청자 이름
    requester_image_url: string; // 요청자 이미지 URL
    report_type: ReportType; // daily / weekly
    workspace_id: string; // 워크스페이스 ID
    workspace_name: string; // 워크스페이스 이름
    workspace_image_url: string; // 워크스페이스 이미지 URL
}

// 피드백 수신 알림 콘텐츠
export interface FeedbackReceivedContent {
    is_updated: boolean; // 수정 알림인지 여부
    feedback_id: string; // 피드백 ID
    feedback_content: string; // 피드백 내용 일부
    writer_id: string; // 작성자 ID
    writer_name: string; // 작성자 이름
    writer_image_url: string; // 작성자 이미지 URL
    workspace_id: string; // 워크스페이스 ID
    workspace_name: string; // 워크스페이스 이름
    workspace_image_url: string; // 워크스페이스 이미지 URL
}

// 피드백 이모지 리액션 알림 콘텐츠
export interface FeedbackReactionContent {
    reaction_content: string[]; // 리액션 내용 (아이콘들)
    is_feedback_receiver: boolean; // 알림 수신자가 피드백 수신자인지 여부 (클릭 후 이동할 탭 체크)
    feedback_id: string; // 피드백 ID
    feedback_content: string; // 피드백 내용 일부
    workspace_id: string; // 워크스페이스 ID
    reactioner_id: string; // 리액션 주체 ID
    reactioner_name: string; // 리액션 주체 이름
    reactioner_image_url: string; // 리액션 주체 이미지 URL
}

// 피드백 요청 알림 콘텐츠
export interface FeedbackRequestContent {
    is_updated: boolean; // 수정 알림인지 여부
    request_id: string; // 요청 ID
    request_content: string; // 요청 내용 일부
    requester_id: string; // 요청자 ID
    requester_name: string; // 요청자 이름
    requester_image_url: string; // 요청자 이미지 URL
    workspace_id: string; // 워크스페이스 ID
    workspace_name: string; // 워크스페이스 이름
    workspace_image_url: string; // 워크스페이스 이미지 URL
}

// 피드백 요청 거절 알림 콘텐츠
export interface FeedbackRequestRejectedContent {
    request_id: string; // 피드백 요청 ID
    rejecter_id: string; // 거절자(=평가자) ID
    rejecter_name: string;
    rejecter_image_url: string;
    // content: string; // 거절 내용
    workspace_id: string; // 워크스페이스 ID
    workspace_name: string; // 워크스페이스 이름
    workspace_image_url: string; // 워크스페이스 이미지 URL
}

// 계획 시간 알림 콘텐츠
export interface PlanTimeContent {
    time: string; // HH:mm 형태
    weekday: number[]; // 0(일) ~ 6(토)
    recurrence_type: string; // 'weekly'
    notification_type: string; // 'plan_time'
}

// 계획 수립 요청 알림 콘텐츠
export interface PlanRequestContent {
    requester_name: string;
    requester_image_url: string;
    requester_id: string;
    team_id: string;
    team_name: string;
    team_image_url: string;
}

// 시스템 공지 알림 콘텐츠
export interface SystemAnnouncementContent {
    title: string;
    message: string;
    importance: 'low' | 'medium' | 'high';
}

// 계정 보안 알림 콘텐츠
export interface AccountSecurityContent {
    type: 'login_from_new_device' | 'password_changed' | 'email_changed';
    detail: string;
    ip_address?: string;
    device_info?: string;
}

// 타입 매핑을 통한 알림 유형과 콘텐츠 연결
export interface NotificationTypeToContent {
    [NotificationType.WORKSPACE_INVITE]: WorkspaceInviteContent;
    [NotificationType.WORKSPACE_JOIN]: WorkspaceJoinContent;
    [NotificationType.WORKSPACE_OUT]: WorkspaceOutContent;

    [NotificationType.TEAM_INVITE]: TeamInviteContent;
    [NotificationType.TEAM_OUT]: TeamOutContent;

    [NotificationType.GOAL_PRIORITY_CHANGED]: GoalPriorityChangedContent;
    [NotificationType.GOAL_STATE_CHANGED]: GoalStateChangedContent;
    [NotificationType.GOAL_DEADLINE_CHANGED]: GoalDeadlineChangedContent;
    [NotificationType.GOAL_LEADER_CHANGED]: GoalLeaderChangedContent;
    [NotificationType.GOAL_DEADLINE_REMINDER]: GoalDeadlineReminderContent;

    [NotificationType.TODO_ASSIGNED]: TodoAssignedContent;
    [NotificationType.TODO_STATE_CHANGED]: TodoStateChangedContent;
    [NotificationType.TODO_DEADLINE_CHANGED]: TodoDeadlineChangedContent;
    [NotificationType.TODO_DEADLINE_REMINDER]: TodoDeadlineReminderContent;
    [NotificationType.TODO_TIME_CHANGED]: TodoTimeChangedContent;

    [NotificationType.PROJECT_PRIORITY_CHANGED]: ProjectPriorityChangedContent;
    [NotificationType.PROJECT_STATE_CHANGED]: ProjectStateChangedContent;
    [NotificationType.PROJECT_DEADLINE_CHANGED]: ProjectDeadlineChangedContent;
    [NotificationType.PROJECT_LEADER_CHANGED]: ProjectLeaderChangedContent;
    [NotificationType.PROJECT_DEADLINE_REMINDER]: ProjectDeadlineReminderContent;

    [NotificationType.COMMENT]: CommentContent;
    [NotificationType.REPLY]: ReplyContent;
    [NotificationType.MENTION]: MentionContent;
    [NotificationType.COMMENT_MENTION]: CommentMentionContent;

    [NotificationType.FEEDBACK_COMMENT]: FeedbackCommentContent;
    [NotificationType.DAILY_SCRUM_COMMENT]: DailyScrumCommentContent;

    [NotificationType.REPORT_RECEIVE]: ReportContent;
    [NotificationType.REPORT_UPDATED]: ReportContent;
    [NotificationType.REPORT_REQUEST]: ReportRequestContent;

    [NotificationType.FEEDBACK_RECEIVED]: FeedbackReceivedContent;
    [NotificationType.FEEDBACK_UPDATED]: FeedbackReceivedContent;
    [NotificationType.FEEDBACK_REACTION]: FeedbackReactionContent;
    [NotificationType.FEEDBACK_REQUEST]: FeedbackRequestContent;
    [NotificationType.FEEDBACK_REQUEST_UPDATED]: FeedbackRequestContent;
    [NotificationType.FEEDBACK_REQUEST_REJECTED]: FeedbackRequestRejectedContent;

    [NotificationType.PLAN_TIME]: PlanTimeContent;
    [NotificationType.PLAN_REQUEST]: PlanRequestContent;

    [NotificationType.SYSTEM_ANNOUNCEMENT]: SystemAnnouncementContent;
    [NotificationType.ACCOUNT_SECURITY]: AccountSecurityContent;
}

/**
 * 알림 유형별 기본 relatedType 반환
 * @param type 알림 유형
 * @returns relatedType
 */
export function getDefaultRelatedType(type: NotificationType): string {
    switch (type) {
        case NotificationType.WORKSPACE_INVITE:
        case NotificationType.WORKSPACE_JOIN:
        case NotificationType.WORKSPACE_OUT:
            return 'workspace';

        case NotificationType.TEAM_INVITE:
        case NotificationType.TEAM_OUT:
            return 'team';

        case NotificationType.GOAL_PRIORITY_CHANGED:
        case NotificationType.GOAL_STATE_CHANGED:
        case NotificationType.GOAL_DEADLINE_CHANGED:
        case NotificationType.GOAL_LEADER_CHANGED:
        case NotificationType.GOAL_DEADLINE_REMINDER:
            return 'goal';

        case NotificationType.TODO_ASSIGNED:
        case NotificationType.TODO_STATE_CHANGED:
        case NotificationType.TODO_DEADLINE_CHANGED:
        case NotificationType.TODO_DEADLINE_REMINDER:
        case NotificationType.TODO_TIME_CHANGED:
            return 'todo';

        case NotificationType.PROJECT_PRIORITY_CHANGED:
        case NotificationType.PROJECT_STATE_CHANGED:
        case NotificationType.PROJECT_DEADLINE_CHANGED:
        case NotificationType.PROJECT_LEADER_CHANGED:
        case NotificationType.PROJECT_DEADLINE_REMINDER:
            return 'project';

        case NotificationType.COMMENT:
        case NotificationType.REPLY:
        case NotificationType.COMMENT_MENTION:
            return 'comment';

        case NotificationType.MENTION:
            return 'note';

        case NotificationType.FEEDBACK_COMMENT:
            return 'feedback_comment';
        case NotificationType.DAILY_SCRUM_COMMENT:
            return 'daily_scrum_comment';

        case NotificationType.REPORT_RECEIVE:
        case NotificationType.REPORT_UPDATED:
        case NotificationType.REPORT_REQUEST:
            return 'report';

        case NotificationType.FEEDBACK_RECEIVED:
        case NotificationType.FEEDBACK_UPDATED:
        case NotificationType.FEEDBACK_REACTION:
            // return 'feedback_emoji_reaction';
            return 'feedback';
        case NotificationType.FEEDBACK_REQUEST:
        case NotificationType.FEEDBACK_REQUEST_UPDATED:
        case NotificationType.FEEDBACK_REQUEST_REJECTED:
            return 'feedback_request';

        case NotificationType.PLAN_TIME:
        case NotificationType.PLAN_REQUEST:
            return 'user';

        default:
            return 'system';
    }
}

/**
 * 알림 콘텐츠에서 실제 이미지 URL 추출
 * @param content 알림 콘텐츠
 * @param type 알림 유형
 * @returns 이미지 URL 또는 null
 */
export const getNotificationActualImageUrl = (
    content: NotificationContent,
    type: NotificationType
): string | null => {
    switch (type) {
        case NotificationType.WORKSPACE_INVITE:
        case NotificationType.WORKSPACE_OUT:
            return (
                (content as WorkspaceInviteContent).workspace_image_url || null
            );
        case NotificationType.WORKSPACE_JOIN:
            return (content as WorkspaceJoinContent).joiner_image_url || null;

        case NotificationType.TEAM_INVITE:
        case NotificationType.TEAM_OUT:
            return (content as TeamInviteContent).team_image_url || null;

        // 목표, 프로젝트는 아이콘 사용
        // case NotificationType.GOAL_PRIORITY_CHANGED:
        // case NotificationType.GOAL_STATE_CHANGED:
        // case NotificationType.GOAL_DEADLINE_CHANGED:
        // case NotificationType.GOAL_LEADER_CHANGED:
        //     return (
        //         (content as GoalPriorityChangedContent).changer_image_url ||
        //         null
        //     );
        // case NotificationType.PROJECT_PRIORITY_CHANGED:
        // case NotificationType.PROJECT_STATE_CHANGED:
        // case NotificationType.PROJECT_DEADLINE_CHANGED:
        // case NotificationType.PROJECT_LEADER_CHANGED:
        //     return (
        //         (content as ProjectPriorityChangedContent).changer_image_url ||
        //         null
        //     );

        // 투두도 아이콘 사용
        // case NotificationType.TODO_ASSIGNED:
        //     return (content as TodoAssignedContent).assigner_image_url || null;
        // case NotificationType.TODO_STATE_CHANGED:
        // case NotificationType.TODO_DEADLINE_CHANGED:
        // case NotificationType.TODO_TIME_CHANGED:
        //     return (
        //         (content as TodoStateChangedContent).changer_image_url || null
        //     );

        case NotificationType.COMMENT:
            return (content as CommentContent).commenter_image_url || null;
        case NotificationType.REPLY:
            return (content as ReplyContent).replier_image_url || null;
        case NotificationType.MENTION:
            return (content as MentionContent).mentioner_image_url || null;
        case NotificationType.COMMENT_MENTION:
            return (
                (content as CommentMentionContent).mentioner_image_url || null
            );

        case NotificationType.FEEDBACK_COMMENT:
            return (
                (content as FeedbackCommentContent).commenter_image_url || null
            );
        case NotificationType.DAILY_SCRUM_COMMENT:
            return (
                (content as DailyScrumCommentContent).commenter_image_url ||
                null
            );

        case NotificationType.REPORT_RECEIVE:
        case NotificationType.REPORT_UPDATED:
            return (content as ReportContent).writer_image_url || null;

        case NotificationType.FEEDBACK_REACTION:
            return (
                (content as FeedbackReactionContent).reactioner_image_url ||
                null
            );
        case NotificationType.REPORT_REQUEST:
            return (
                (content as ReportRequestContent).requester_image_url || null
            );

        case NotificationType.FEEDBACK_RECEIVED:
        case NotificationType.FEEDBACK_UPDATED:
            return (
                (content as FeedbackReceivedContent).writer_image_url || null
            );

        case NotificationType.FEEDBACK_REQUEST:
        case NotificationType.FEEDBACK_REQUEST_UPDATED:
            return (
                (content as FeedbackRequestContent).requester_image_url || null
            );
        case NotificationType.FEEDBACK_REQUEST_REJECTED:
            return (
                (content as FeedbackRequestRejectedContent)
                    .rejecter_image_url || null
            );

        default:
            return null;
    }
};

// getNotificationDefaultIcon에서 리턴할 인터페이스
export interface NotificationIconInfo {
    icon: ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;
    colorName: string;
}

/**
 * 알림 타입에 따른 기본 아이콘 이름 반환
 * @param type 알림 유형
 * @returns 아이콘 이름 (Icon 컴포넌트에 넘길 수 있는 이름)
 */
export const getNotificationDefaultIcon = (
    type: NotificationType
): NotificationIconInfo => {
    switch (type) {
        case NotificationType.WORKSPACE_INVITE:
        case NotificationType.WORKSPACE_JOIN:
        case NotificationType.WORKSPACE_OUT:
        case NotificationType.TEAM_INVITE:
        case NotificationType.TEAM_OUT:
            return { icon: IconUsers, colorName: 'indigo' };

        case NotificationType.GOAL_PRIORITY_CHANGED:
        case NotificationType.GOAL_STATE_CHANGED:
        case NotificationType.GOAL_DEADLINE_CHANGED:
        case NotificationType.GOAL_LEADER_CHANGED:
        case NotificationType.GOAL_DEADLINE_REMINDER:
            return { icon: IconSwipe, colorName: 'cyan' };

        case NotificationType.TODO_ASSIGNED:
        case NotificationType.TODO_STATE_CHANGED:
        case NotificationType.TODO_DEADLINE_CHANGED:
        case NotificationType.TODO_TIME_CHANGED:
        case NotificationType.TODO_DEADLINE_REMINDER:
        case NotificationType.PLAN_TIME:
        case NotificationType.PLAN_REQUEST:
            return { icon: IconChecklist, colorName: 'orange' };

        case NotificationType.PROJECT_PRIORITY_CHANGED:
        case NotificationType.PROJECT_STATE_CHANGED:
        case NotificationType.PROJECT_DEADLINE_CHANGED:
        case NotificationType.PROJECT_LEADER_CHANGED:
        case NotificationType.PROJECT_DEADLINE_REMINDER:
            return { icon: IconPresentation, colorName: 'pink' };

        case NotificationType.COMMENT:
        case NotificationType.REPLY:
        case NotificationType.MENTION:
        case NotificationType.COMMENT_MENTION:
            return { icon: IconUserCircle, colorName: 'gray' };

        default:
            return { icon: IconUserCircle, colorName: 'gray' };
    }
};

/**
 * 알림 메시지 표시 함수에서 반환할 인터페이스 - column 형태로 배치됨
 * message: 알림 메시지 컴포넌트
 * additionalContent: 알림 메시지 컴포넌트 외의 추가 컴포넌트 (리액션 이모지 그룹, 버튼 컴포넌트 등)
 */
export interface NotificationMessageResult {
    message: React.ReactNode;
    additionalContent?: React.ReactNode;
}
/**
 * 알림 메시지 표시 함수
 * @param type 알림 유형
 * @param content 알림 콘텐츠
 * @returns 사용자에게 보여줄 알림 메시지 컴포넌트
 */
export const getNotificationMessage = (
    type: NotificationType,
    content: NotificationContent
): NotificationMessageResult => {
    // // i18next.t()는 string | object 등을 반환할 수 있으므로 타입 캐스팅을 사용합니다
    // const t = (key: string, options?: Record<string, unknown>): string => {
    //     return i18next.t(`common:${key}`, options).toString();
    // };
    const textComponents = [
        <Text component="span" size="xs" fw={600} key="bold" />,
        <Text component="span" size="xs" key="light" />,
    ];

    switch (type) {
        // 워크스페이스 관련
        case NotificationType.WORKSPACE_INVITE:
            return {
                message: (
                    <Trans
                        i18nKey="notification:workspace.invite"
                        components={textComponents}
                        values={{
                            inviter_name: (content as WorkspaceInviteContent)
                                .inviter_name,
                            workspace_name: (content as WorkspaceInviteContent)
                                .workspace_name,
                        }}
                    />
                ),
            };
        case NotificationType.WORKSPACE_JOIN:
            return {
                message: (
                    <Trans
                        i18nKey="notification:workspace.join"
                        components={textComponents}
                        values={{
                            joiner_name: (content as WorkspaceJoinContent)
                                .joiner_name,
                            workspace_name: (content as WorkspaceJoinContent)
                                .workspace_name,
                        }}
                    />
                ),
            };

        case NotificationType.WORKSPACE_OUT:
            return {
                message: (
                    <Trans
                        i18nKey="notification:workspace.out"
                        components={textComponents}
                        values={{
                            workspace_name: (content as WorkspaceOutContent)
                                .workspace_name,
                        }}
                    />
                ),
            };

        // 팀 관련
        case NotificationType.TEAM_INVITE:
            return {
                message: (
                    <Trans
                        i18nKey="notification:team.invite"
                        components={textComponents}
                        values={{
                            inviter_name: (content as TeamInviteContent)
                                .inviter_name,
                            workspace_name: (content as TeamInviteContent)
                                .workspace_name,
                            team_name: (content as TeamInviteContent).team_name,
                        }}
                    />
                ),
            };
        case NotificationType.TEAM_OUT:
            return {
                message: (
                    <Trans
                        i18nKey="notification:team.out"
                        components={textComponents}
                        values={{
                            workspace_name: (content as TeamOutContent)
                                .workspace_name,
                            team_name: (content as TeamOutContent).team_name,
                        }}
                    />
                ),
            };

        // 목표 관련
        case NotificationType.GOAL_PRIORITY_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:task.priorityChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as GoalPriorityChangedContent
                            ).changer_name,
                            goal_title: limitNotificationMessage(
                                (content as GoalPriorityChangedContent)
                                    .goal_title
                            ),
                            new_priority: PriorityCodeLabel[
                                (content as GoalPriorityChangedContent)
                                    .new_priority
                            ] as string,
                        }}
                    />
                ),
            };
        case NotificationType.GOAL_STATE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:task.stateChanged"
                        components={textComponents}
                        values={{
                            changer_name: (content as GoalStateChangedContent)
                                .changer_name,
                            goal_title: limitNotificationMessage(
                                (content as GoalStateChangedContent).goal_title
                            ),
                            new_state: StatusCodeLabel[
                                (content as GoalStateChangedContent).new_state
                            ] as string,
                        }}
                    />
                ),
            };
        case NotificationType.GOAL_DEADLINE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:task.deadlineChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as GoalDeadlineChangedContent
                            ).changer_name,
                            goal_title: limitNotificationMessage(
                                (content as GoalDeadlineChangedContent)
                                    .goal_title
                            ),
                            date_range: dateRangeDisplay(
                                (content as GoalDeadlineChangedContent)
                                    .new_start_date,
                                (content as GoalDeadlineChangedContent)
                                    .new_end_date
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.GOAL_LEADER_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:task.leaderChanged"
                        components={textComponents}
                        values={{
                            goal_title: limitNotificationMessage(
                                (content as GoalLeaderChangedContent).goal_title
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.GOAL_DEADLINE_REMINDER: {
            const {
                title: goal_title,
                deadline_status,
                days_delta,
            } = content as GoalDeadlineReminderContent;

            return {
                message: (
                    <Trans
                        i18nKey={`notification:task.deadlineReminder.${deadline_status}`}
                        components={textComponents}
                        values={{
                            title: limitNotificationMessage(goal_title),
                            days_delta: days_delta,
                        }}
                    />
                ),
            };
        }

        // 할 일 관련
        case NotificationType.TODO_ASSIGNED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:todo.assigned"
                        components={textComponents}
                        values={{
                            assigner_name: (content as TodoAssignedContent)
                                .assigner_name,
                            todo_title: limitNotificationMessage(
                                (content as TodoAssignedContent).todo_title
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.TODO_STATE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:todo.stateChanged"
                        components={textComponents}
                        values={{
                            changer_name: (content as TodoStateChangedContent)
                                .changer_name,
                            todo_title: limitNotificationMessage(
                                (content as TodoStateChangedContent).todo_title
                            ),
                            new_state: TodoStatusCodeLabel[
                                (content as TodoStateChangedContent).new_state
                            ] as string,
                        }}
                    />
                ),
            };
        case NotificationType.TODO_DEADLINE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:todo.deadlineChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as TodoDeadlineChangedContent
                            ).changer_name,
                            todo_title: limitNotificationMessage(
                                (content as TodoDeadlineChangedContent)
                                    .todo_title
                            ),
                            date_range: dateRangeDisplay(
                                null,
                                (content as TodoDeadlineChangedContent)
                                    .new_deadline
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.TODO_DEADLINE_REMINDER: {
            const {
                title: todo_title,
                deadline_status,
                days_delta,
            } = content as TodoDeadlineReminderContent;

            return {
                message: (
                    <Trans
                        i18nKey={`notification:todo.deadlineReminder.${deadline_status}`}
                        components={textComponents}
                        values={{
                            title: limitNotificationMessage(todo_title),
                            days_delta: days_delta,
                        }}
                    />
                ),
            };
        }
        case NotificationType.TODO_TIME_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:todo.timeChanged"
                        components={textComponents}
                        values={{
                            changer_name: (content as TodoTimeChangedContent)
                                .changer_name,
                            todo_title: limitNotificationMessage(
                                (content as TodoTimeChangedContent).todo_title
                            ),
                        }}
                    />
                ),
            };

        // 프로젝트 관련
        case NotificationType.PROJECT_PRIORITY_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:project.priorityChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as ProjectPriorityChangedContent
                            ).changer_name,
                            project_title: limitNotificationMessage(
                                (content as ProjectPriorityChangedContent)
                                    .project_title
                            ),
                            new_priority: PriorityCodeLabel[
                                (content as ProjectPriorityChangedContent)
                                    .new_priority
                            ] as string,
                        }}
                    />
                ),
            };
        case NotificationType.PROJECT_STATE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:project.stateChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as ProjectStateChangedContent
                            ).changer_name,
                            project_title: limitNotificationMessage(
                                (content as ProjectStateChangedContent)
                                    .project_title
                            ),
                            new_state: StatusCodeLabel[
                                (content as ProjectStateChangedContent)
                                    .new_state
                            ] as string,
                        }}
                    />
                ),
            };
        case NotificationType.PROJECT_DEADLINE_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:project.deadlineChanged"
                        components={textComponents}
                        values={{
                            changer_name: (
                                content as ProjectDeadlineChangedContent
                            ).changer_name,
                            project_title: limitNotificationMessage(
                                (content as ProjectDeadlineChangedContent)
                                    .project_title
                            ),
                            date_range: dateRangeDisplay(
                                (content as ProjectDeadlineChangedContent)
                                    .new_start_date,
                                (content as ProjectDeadlineChangedContent)
                                    .new_end_date
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.PROJECT_LEADER_CHANGED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:project.leaderChanged"
                        components={textComponents}
                        values={{
                            project_title: limitNotificationMessage(
                                (content as ProjectLeaderChangedContent)
                                    .project_title
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.PROJECT_DEADLINE_REMINDER: {
            const {
                title: project_title,
                deadline_status,
                days_delta,
            } = content as ProjectDeadlineReminderContent;

            return {
                message: (
                    <Trans
                        i18nKey={`notification:project.deadlineReminder.${deadline_status}`}
                        components={textComponents}
                        values={{
                            title: limitNotificationMessage(project_title),
                            days_delta: days_delta,
                        }}
                    />
                ),
            };
        }

        // 커뮤니케이션 관련
        case NotificationType.COMMENT: {
            const notificationContent = content as CommentContent;
            const parentType = notificationContent.parent_type || 'default';

            return {
                message: (
                    <Trans
                        i18nKey={`notification:communication.comment.${parentType}`}
                        components={textComponents}
                        values={{
                            commenter_name: notificationContent.commenter_name,
                            parent_title: limitNotificationMessage(
                                notificationContent.parent_title
                            ),
                            comment_text: limitNotificationMessage(
                                notificationContent.comment_text
                            ),
                        }}
                    />
                ),
            };
        }
        case NotificationType.REPLY:
            return {
                message: (
                    <Trans
                        i18nKey="notification:communication.reply"
                        components={textComponents}
                        values={{
                            replier_name: (content as ReplyContent)
                                .replier_name,
                            reply_text: limitNotificationMessage(
                                (content as ReplyContent).reply_text
                            ),
                        }}
                    />
                ),
            };
        // return `${(content as ReplyContent).replier_name}님이 ${(content as ReplyContent).parent_title}에 답글을 남겼어요. '${(content as ReplyContent).reply_text}'`;
        case NotificationType.MENTION:
            return {
                message: (
                    <Trans
                        i18nKey="notification:communication.mention"
                        components={textComponents}
                        values={{
                            mentioner_name: (content as MentionContent)
                                .mentioner_name,
                            context_title: limitNotificationMessage(
                                (content as MentionContent).context_title
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.COMMENT_MENTION:
            return {
                message: (
                    <Trans
                        i18nKey={`notification:communication.comment_mention.${(content as CommentMentionContent).parent_type}`}
                        components={textComponents}
                        values={{
                            mentioner_name: (content as CommentMentionContent)
                                .mentioner_name,
                            context_title: limitNotificationMessage(
                                (content as CommentMentionContent).parent_title
                            ),
                            comment_text: limitNotificationMessage(
                                (content as CommentMentionContent).comment_text
                            ),
                        }}
                    />
                ),
            };

        case NotificationType.FEEDBACK_COMMENT:
            return {
                message: (
                    <Trans
                        i18nKey={
                            (content as FeedbackCommentContent).is_reply
                                ? 'notification:communication.feedback_reply'
                                : 'notification:communication.feedback_comment'
                        }
                        components={textComponents}
                        values={{
                            commenter_name: (content as FeedbackCommentContent)
                                .commenter_name,
                            feedback_content: limitNotificationMessage(
                                (content as FeedbackCommentContent)
                                    .feedback_content
                            ),
                            comment_text: limitNotificationMessage(
                                (content as FeedbackCommentContent).comment_text
                            ),
                        }}
                    />
                ),
            };

        case NotificationType.DAILY_SCRUM_COMMENT:
            return {
                message: (
                    <Trans
                        i18nKey={
                            (content as DailyScrumCommentContent).is_reply
                                ? 'notification:communication.daily_scrum_reply'
                                : 'notification:communication.daily_scrum_comment'
                        }
                        components={textComponents}
                        values={{
                            commenter_name: (
                                content as DailyScrumCommentContent
                            ).commenter_name,
                            daily_scrum_date: (
                                content as DailyScrumCommentContent
                            ).daily_scrum_date,
                            comment_text: limitNotificationMessage(
                                (content as DailyScrumCommentContent)
                                    .comment_text
                            ),
                        }}
                    />
                ),
            };

        // 일정 수립 관련
        case NotificationType.PLAN_TIME:
            return {
                message: (
                    <Trans
                        i18nKey="notification:todo.plan_time"
                        components={textComponents}
                    />
                ),
            };

        case NotificationType.PLAN_REQUEST:
            return {
                message: (
                    <Trans
                        i18nKey="notification:communication.plan_request"
                        components={textComponents}
                        values={{
                            requester_name: (content as PlanRequestContent)
                                .requester_name,
                        }}
                    />
                ),
            };

        // 성과관리 관련
        case NotificationType.REPORT_RECEIVE:
            return {
                message: (
                    <Trans
                        i18nKey="notification:performance.report_receive"
                        components={textComponents}
                        values={{
                            writer_name: (content as ReportContent).writer_name,
                            report_type:
                                ReportTypeLabel[
                                    (content as ReportContent).report_type
                                ],
                        }}
                    />
                ),
            };
        case NotificationType.REPORT_UPDATED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:performance.report_updated"
                        components={textComponents}
                        values={{
                            writer_name: (content as ReportContent).writer_name,
                            report_type:
                                ReportTypeLabel[
                                    (content as ReportContent).report_type
                                ],
                        }}
                    />
                ),
            };
        case NotificationType.REPORT_REQUEST:
            return {
                message: (
                    <Trans
                        i18nKey="notification:performance.report_request"
                        components={textComponents}
                        values={{
                            requester_name: (content as ReportRequestContent)
                                .requester_name,
                            report_type:
                                ReportTypeLabel[
                                    (content as ReportRequestContent)
                                        .report_type
                                ],
                        }}
                    />
                ),
            };

        case NotificationType.FEEDBACK_RECEIVED:
        case NotificationType.FEEDBACK_UPDATED:
            return {
                message: (
                    <Trans
                        i18nKey={
                            (content as FeedbackReceivedContent).is_updated
                                ? 'notification:performance.feedback_updated'
                                : 'notification:performance.feedback_received'
                        }
                        components={textComponents}
                        values={{
                            writer_name: (content as FeedbackReceivedContent)
                                .writer_name,
                            content: limitNotificationMessage(
                                (content as FeedbackReceivedContent)
                                    .feedback_content
                            ),
                        }}
                    />
                ),
            };
        case NotificationType.FEEDBACK_REACTION:
            return {
                message: (
                    <Trans
                        i18nKey="notification:performance.feedback_reaction"
                        components={textComponents}
                        values={{
                            reactioner_name: (
                                content as FeedbackReactionContent
                            ).reactioner_name,
                            feedback_content: limitNotificationMessage(
                                (content as FeedbackReactionContent)
                                    .feedback_content
                            ),
                        }}
                    />
                ),
                additionalContent: (
                    <Group gap={4}>
                        {(
                            content as FeedbackReactionContent
                        ).reaction_content.map(reaction => {
                            const _key = `${(content as FeedbackReactionContent).feedback_id}-${reaction}`;
                            return (
                                <Pill
                                    key={_key}
                                    styles={{
                                        label: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        },
                                    }}
                                    style={{
                                        backgroundColor: 'gray.0',
                                    }}
                                >
                                    <Text size="xs">{reaction}</Text>
                                </Pill>
                            );
                        })}
                    </Group>
                ),
            };
        case NotificationType.FEEDBACK_REQUEST:
        case NotificationType.FEEDBACK_REQUEST_UPDATED:
            return {
                message: (
                    <Trans
                        i18nKey={
                            (content as FeedbackRequestContent).is_updated
                                ? 'notification:performance.feedback_request_updated'
                                : 'notification:performance.feedback_request'
                        }
                        components={textComponents}
                        values={{
                            requester_name: (content as FeedbackRequestContent)
                                .requester_name,
                            content: limitNotificationMessage(
                                (content as FeedbackRequestContent)
                                    .request_content
                            ),
                        }}
                    />
                ),
            };

        case NotificationType.FEEDBACK_REQUEST_REJECTED:
            return {
                message: (
                    <Trans
                        i18nKey="notification:performance.feedback_request_rejected"
                        components={textComponents}
                        values={{
                            name: (content as FeedbackRequestRejectedContent)
                                .rejecter_name,
                        }}
                    />
                ),
            };

        // 시스템 알림
        case NotificationType.SYSTEM_ANNOUNCEMENT:
            return {
                message: (
                    <Trans
                        i18nKey="notification:system.announcement"
                        components={textComponents}
                        values={{
                            title: (content as SystemAnnouncementContent).title,
                        }}
                    />
                ),
            };

        default:
            return {
                message: (
                    <Trans
                        i18nKey="notification:title"
                        components={textComponents}
                    />
                ),
            };
    }
};
